import type React from 'react';
import { cn } from '../../utils/cn.js';

/**
 * Plain controls for places that are not a validated form (inline filters, editors inside a builder). Inside a form use
 * the field binders in `components/form`. Styled like the vendored TailGrids input (`tailgrids/core/input.tsx`); the
 * caller's `className` is merged with `cn`, so an override like `text-xs` simply wins.
 *
 * `invalid` turns the border and focus ring to the error colour and sets `aria-invalid`; the caller renders the
 * message.
 */
const FIELD =
  'w-full rounded-lg border border-card-border bg-input-background px-3 py-2 text-sm text-title-50 outline-none ' +
  'transition placeholder:text-input-placeholder-text focus:border-input-primary-focus-border focus:ring-4 ' +
  'focus:ring-input-primary-focus-border/20 disabled:cursor-not-allowed disabled:text-input-disabled-text';
const INVALID =
  'border-input-error-focus-border focus:border-input-error-focus-border focus:ring-input-error-focus-border/20';

// React 19: `ref` is a prop; declared so callers can focus the control.
type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
  React.RefAttributes<HTMLInputElement> & { invalid?: boolean };

export function Input({ className, invalid, ...props }: InputProps) {
  return <input aria-invalid={invalid || undefined} className={cn(FIELD, invalid && INVALID, className)} {...props} />;
}

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  React.RefAttributes<HTMLTextAreaElement> & { invalid?: boolean };

export function Textarea({ className, invalid, ...props }: TextareaProps) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={cn(FIELD, 'resize-none', invalid && INVALID, className)}
      {...props}
    />
  );
}

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> &
  React.RefAttributes<HTMLSelectElement> & { invalid?: boolean };

export function Select({ className, invalid, children, ...props }: SelectProps) {
  return (
    <select aria-invalid={invalid || undefined} className={cn(FIELD, 'pr-8', invalid && INVALID, className)} {...props}>
      {children}
    </select>
  );
}
